/**
 * The strategy card's Annual Snapshot — six figures, each carrying its own explanation.
 *
 * WHY THIS EXISTS. The six numbers had hover tooltips with the calculation, but the PLAIN-WORDS
 * explanation of each figure lived only in the closable Insight Panel — and a client who closed it
 * (2026-08-20) had no way back to "why is the pension higher here?". Owner's direction (2026-08-24):
 * combine the two — a tap opens the WHY in client-friendly words, with the CALCULATION one layer
 * deeper for whoever wants to audit the arithmetic. The hover tooltips went on 18 Oct 2026 (owner: two copies of the same
 * rows); the tap layer is the one path, and it works on the iPads advisers demo on.
 *
 * The words are TRANSPOSED from the Insight Panel's own vetted narratives (illness-separated
 * couple, the basic daily fee, the deposit trade) — not new content. The calculation layer renders
 * the SAME breakdown arrays the tooltips show (`agePensionBreakdown`, `agedCareBreakdown`,
 * `taxBreakdown`), so the two layers can never disagree.
 *
 * Frame discipline (learned the hard way on the walk-in note, 2026-08-24): every number quoted in
 * a why is this card's own snapshot figure. No second assessment frame is ever mixed in.
 */
import { enteredAmount, scenarioLivingExpensesOr } from '../../../../shared/src/calculation/support/livingExpenses.ts';
import React, { useEffect, useState } from 'react';
import { type TooltipBreakdownRow } from '../../components/ui/CalculationTooltip.tsx';
import { JargonTerm } from '../../components/narrative/index.tsx';
import { hasAgedCareProtectedPerson } from '../../../../shared/src/calculation/entityHomeRules.ts';
import type { FactFind } from '../../../../shared/src/calculation/factFindTypes.ts';
import { formatCurrency } from '../../utils/index.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import type { EvaluatedStrategy } from './futureLifeEventUtils.tsx';

export type SnapshotTileKey = 'pension' | 'invest' | 'living' | 'care' | 'tax' | 'cf';
type TileKey = SnapshotTileKey;

/** The walk-in position's six figures, for the line under each tile. */
export interface SnapshotBaseline {
    pension: number;
    investmentIncome: number;
    livingExpenses: number;
    careCosts: number;
    taxPayable: number;
    netCashflow: number;
}

interface AnnualSnapshotProps {
    strategy: EvaluatedStrategy;
    agedCareCosts: { scenarioLivingExpenses?: number | string };
    factFind: DraftFactFind;
    /**
     * Doing nothing, for comparison. With it, every tile carries "walk-in $X · +$Y" beneath its figure,
     * which is what lets the Suggestion Engine's rail hold the walk-in position instead of repeating
     * the selected plan (owner, 2026-09-22). Without it the tiles show the plan's figures alone.
     */
    baseline?: SnapshotBaseline | null;
    /** A "why?" on the card's headline band asks for its tile to open here (the band is a shortcut to the tiles). */
    openRequest?: { tile: SnapshotTileKey; nonce: number } | null;
}

/**
 * The line under a tile: the walk-in figure and the change, coloured by whether the move is good —
 * up is good for money in, down is good for money out.
 */
const WalkIn = ({ value, baseline, upIsGood }: { value: number; baseline: number | undefined; upIsGood: boolean }) => {
    if (baseline == null) return null;
    const d = value - baseline;
    const flat = Math.round(d) === 0;
    const good = upIsGood ? d > 0 : d < 0;
    return (
        <p className="mt-1 text-[13px] tabular-nums text-slate-500">
            walk-in {formatCurrency(baseline)} ·{' '}
            <span className={`font-semibold ${flat ? 'text-slate-400' : good ? 'text-emerald-700' : 'text-rose-700'}`}>
                {flat ? 'no change' : `${d > 0 ? '+' : '\u2212'}${formatCurrency(Math.abs(d))}`}
            </span>
        </p>
    );
};

/** The care-cost calculation rows: daily, annual, then the engine's own breakdown. */
export function careRowsOf(snapshot: Record<string, unknown>): TooltipBreakdownRow[] {
    return [
        { label: 'Daily Cost', value: formatCurrency(Math.round((Number(snapshot.totalAgedCareCosts) || 0) / 365)) },
        { label: 'Annual Cost', value: formatCurrency(Number(snapshot.totalAgedCareCosts) || 0) },
        { label: '--- Breakdown ---', value: '' },
        ...((snapshot.agedCareBreakdown as TooltipBreakdownRow[] | undefined) || []),
    ];
}

/** The why layer — the Insight Panel's narratives, re-homed at the number they explain. One source for the tiles and the figure pop-over. */
export function tileWhy(key: SnapshotTileKey, ctx: { isCouple: boolean; protectedPersonHome: boolean }): React.ReactNode {
    switch (key) {
        case 'pension':
            return (
                <>
                    Money paid as a room deposit is <strong>exempt from the assets test</strong>, so the assets
                    Centrelink counts fall by whatever this strategy pays up front — that is why the pension can rise.
                    {ctx.isCouple && (
                        <> And when one partner enters residential care, Centrelink assesses you as an{' '}
                        <JargonTerm term="illness-separated couple" /> — higher thresholds than a couple at home,
                        so a higher figure than the Dashboard shows is expected here.</>
                    )}
                </>
            );
        case 'invest':
            return (
                <>
                    What the money <strong>not</strong> used for the deposit keeps earning. A bigger deposit cuts
                    this line but lifts the pension and shrinks the daily room charge — that is the trade every
                    card on this page is weighing.
                </>
            );
        case 'living':
            return (
                <>
                    Your adjusted living expenses from the top of this page. The same figure sits in every card,
                    so the cards differ only by strategy — never by lifestyle assumptions.
                </>
            );
        case 'care':
            return (
                <>
                    The <JargonTerm term="basic daily fee" /> applies to everyone — it is 85% of the single Age
                    Pension and covers meals, cleaning, laundry and utilities. The means assessment can add the{' '}
                    <JargonTerm term="Hotelling Supplement Contribution" /> and the <JargonTerm term="NCCC" />.
                    The accommodation charge applies only to the part of the room this strategy leaves unpaid —
                    it is the one fee a deposit changes.
                    {ctx.protectedPersonHome && (
                        <> The means-tested care contribution is kept down here because a{' '}
                        <JargonTerm term="protected person" /> lives in the home, keeping it out of the means test.</>
                    )}
                </>
            );
        case 'tax':
            return (
                <>
                    At these income levels the seniors tax offsets usually reduce tax to little or nothing, and a
                    figure shown as a refund means franking credits came back. The calculation shows exactly how
                    this figure was struck.
                </>
            );
        case 'cf':
            return (
                <>
                    Money in (the pension and investment income) minus money out (care and living costs). When it
                    is negative, the gap is drawn from savings — the note under these tiles says when they run low
                    and what pays for care after that.
                </>
            );
    }
}

const AnnualSnapshot = ({ strategy, agedCareCosts, factFind, baseline = null, openRequest = null }: AnnualSnapshotProps) => {
    const [openTile, setOpenTile] = useState<TileKey | null>(null);
    const [showCalc, setShowCalc] = useState(false);
    /** The cashflow pop-over has a second page — how the tax is worked out — reached from its tax line. */
    const [taxPage, setTaxPage] = useState(false);
    useEffect(() => {
        if (!openRequest) return;
        setOpenTile(openRequest.tile);
        setShowCalc(true);
        setTaxPage(false);
    }, [openRequest]);

    const snapshot = strategy.snapshot || {};
    const netCashflow = Number(snapshot.netCashflow ?? 0);
    const taxPayable = Number(snapshot.taxPayable ?? 0);
    // An entered $0 is $0 (livingExpenses.ts; owner-caught 9 Oct 2026: the tile read "$25,000" on a card whose cashflow used $0).
    const livingExpenses = enteredAmount(snapshot.livingExpenses) ?? scenarioLivingExpensesOr(agedCareCosts.scenarioLivingExpenses, factFind.expenses?.livingExpenses);
    const investmentIncomeGross = (Number(snapshot.abpIncome) || 0) + (Number(snapshot.rentalIncome) || 0) + (Number(snapshot.otherInvestmentIncome) || 0);
    /*
     * RENTAL EXPENSES (owner: "this is important", 2026-09-22). A strategy that rents the family home
     * earns the rent inside investment income but pays the expenses out of it, and the expenses had
     * no tile — so the six tiles were that amount apart from the net. The tile now shows the income
     * NET of the rental expenses and says so, and the calculation lists both, so the tiles add up.
     */
    const rentalExpenses = Number(snapshot.rentalExpenses) || 0;
    const investmentIncome = investmentIncomeGross - rentalExpenses;
    const isCouple = factFind.adviceFor === 'couple';
    const protectedPersonHome = hasAgedCareProtectedPerson(factFind as unknown as FactFind, {});

    const hasPartiallyLiquidatedABP = !!strategy.radFunding &&
        Object.values(strategy.radFunding).some((liquidations) =>
            Array.isArray(liquidations)
                ? liquidations.some((l) => l?.assetType === 'accountBasedPensions' && (parseFloat(String(l?.amount)) || 0) > 0)
                : (liquidations as { assetType?: string; amount?: unknown })?.assetType === 'accountBasedPensions'
                    && (parseFloat(String((liquidations as { amount?: unknown })?.amount)) || 0) > 0
        );

    // In the client's words (UX review, 17 Oct 2026): money in, then money out, then what is left — the
    // same seven figures the engine returns, relabelled. The tax line's sign says what it is.
    const cashflowRows: TooltipBreakdownRow[] = [
        { label: '--- Money in ---', value: '' },
        { label: 'Age Pension', value: '+' + formatCurrency(Number(snapshot.pension) || 0) },
        { label: 'Income from the account-based pensions', value: '+' + formatCurrency(Number(snapshot.abpIncome) || 0) },
        { label: 'Other investment income', value: '+' + formatCurrency(Number(snapshot.otherInvestmentIncome) || 0) },
        ...(rentalExpenses > 0 ? [{ label: 'Less: rental expenses', value: '-' + formatCurrency(rentalExpenses) }] : []),
        ...(taxPayable < 0 ? [{ label: 'Tax refund (franking credits come back)', value: '+' + formatCurrency(Math.abs(taxPayable)) }] : []),
        { label: '--- Money out ---', value: '' },
        { label: 'Care costs', value: '-' + formatCurrency(Number(snapshot.totalAgedCareCosts) || 0) },
        { label: 'Living costs at home', value: '-' + formatCurrency(Number(snapshot.livingExpenses) || 0) },
        ...(taxPayable > 0 ? [{ label: 'Tax', value: '-' + formatCurrency(taxPayable) }] : []),
        ...(taxPayable === 0 ? [{ label: 'Tax', value: '$0' }] : []),
        { label: netCashflow >= 0 ? 'Left over each year' : 'Each year, short by', value: (netCashflow >= 0 ? '+' : '') + formatCurrency(netCashflow) },
    ];
    const careRows = careRowsOf(snapshot as Record<string, unknown>);

    /** The calculation layer per tile — the exact same rows the hover tooltip shows. */
    const calcRows: Record<TileKey, TooltipBreakdownRow[]> = {
        pension: (snapshot.agePensionBreakdown as TooltipBreakdownRow[] | undefined) || [{ label: 'No breakdown available for this card.', value: '' }],
        invest: [
            { label: 'ABP Income', value: '+' + formatCurrency(Number(snapshot.abpIncome) || 0) },
            { label: 'Rental Income', value: '+' + formatCurrency(Number(snapshot.rentalIncome) || 0) },
            { label: 'Other Investment Income', value: '+' + formatCurrency(Number(snapshot.otherInvestmentIncome) || 0) },
            { label: 'Total', value: '+' + formatCurrency(investmentIncome) },
        ],
        living: [{ label: 'Adjusted annual living expenses (set at the top of this page)', value: formatCurrency(livingExpenses) }],
        care: careRows,
        tax: (snapshot.taxBreakdown as TooltipBreakdownRow[] | undefined) || [{ label: 'No tax payable.', value: '' }],
        cf: cashflowRows,
    };

    const whyFor = (key: TileKey): React.ReactNode => tileWhy(key, { isCouple, protectedPersonHome });

    const toggleTile = (key: TileKey) => {
        setShowCalc(false);
        setOpenTile((current) => (current === key ? null : key));
    };
    /*
     * TILES THAT SHOW THEIR DIRECTION (owner, 2026-09-01: "this screen looks very bla").
     *
     * Six identical grey boxes made money coming IN look exactly like money going OUT, so the reader
     * had to parse a plus or minus sign on every figure to work out which was which. The tint and the
     * top rule now carry the direction, the figure is set larger in tabular numerals so the column
     * scans down, and the NET result - the one figure that answers "can this be afforded" - is set
     * apart from its six inputs rather than being the sixth identical box.
     *
     * Colour is never the only signal: the sign stays on every figure, and the labels are unchanged.
     */
    const TONES: Record<'in' | 'out' | 'net', { base: string; rule: string; value: string }> = {
        // White tiles (owner, 22 Sep 2026): the coloured rule and the coloured figure already say what
        // each tile means; a pastel fill said it a third time and six of them read as a patchwork.
        in: { base: 'bg-white border-slate-200', rule: 'bg-emerald-400', value: 'text-emerald-700' },
        out: { base: 'bg-white border-slate-200', rule: 'bg-rose-400', value: 'text-rose-700' },
        net: { base: 'bg-white border-slate-200', rule: 'bg-blue-500', value: 'text-slate-900' },
    };
    const tileClass = (key: TileKey, tone: 'in' | 'out' | 'net' = 'in') =>
        `relative overflow-hidden rounded-xl border p-3 pt-3.5 text-center cursor-pointer transition-all `
        + `hover:-translate-y-px hover:shadow-sm ${TONES[tone].base} `
        + `${openTile === key ? 'ring-2 ring-blue-500 ring-offset-1' : ''}`;
    const Rule = ({ tone }: { tone: 'in' | 'out' | 'net' }) => (
        <span aria-hidden="true" className={`absolute inset-x-0 top-0 h-1 ${TONES[tone].rule}`} />
    );
    const valueClass = (tone: 'in' | 'out' | 'net') =>
        `mt-0.5 text-[19px] font-bold tabular-nums leading-tight ${TONES[tone].value}`;

    return (
        <div className="mb-6">
            {/* Full-YEAR rates on a normalised basis — see the git history of the panel for why the
                old "(Year 1)" label was a mislabel an adviser caught against the Deep Dive. */}
            <div className="flex items-baseline justify-between gap-3 flex-wrap mb-1">
                <h4 className="text-lg font-semibold text-gray-800">Annual Snapshot</h4>
                <span className="text-[13px] text-gray-500">A normalised year</span>
            </div>
            <p className="mb-3 text-[13px] text-gray-500">
                A full 12 months with this plan in place, so every strategy compares like for like.
                It is not the first financial year, which starts today and is shorter — <strong>Explain this</strong> has
                the year-by-year figures. <strong>Tap any tile for its why.</strong>
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className={tileClass('pension', 'in')} onClick={() => toggleTile('pension')} role="button" tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleTile('pension'); } }}>
                    <div className="mb-1 text-sm font-medium text-gray-600">Est. Age Pension</div>
                    <Rule tone="in" /><dd className={valueClass('in')}>+ {formatCurrency(Number(snapshot.pension) || 0)}</dd>
                    <WalkIn value={Number(snapshot.pension) || 0} baseline={baseline?.pension} upIsGood />
                </div>
                <div className={tileClass('invest', 'in')} onClick={() => toggleTile('invest')} role="button" tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleTile('invest'); } }}>
                    <div className="text-sm font-medium text-gray-600 mb-1">Investment Income</div>
                    <Rule tone="in" /><dd className={valueClass('in')}>+ {formatCurrency(investmentIncome)}</dd>
                    {rentalExpenses > 0 && (
                        <div className="mt-1 text-[13px] text-gray-500">after {formatCurrency(rentalExpenses)} of rental expenses</div>
                    )}
                    <WalkIn value={investmentIncome} baseline={baseline?.investmentIncome} upIsGood />
                    {hasPartiallyLiquidatedABP && (
                        <div className="text-xs text-gray-500 mt-1 italic">
                            * ABP income recalculated based on minimum drawdown for remaining balance
                        </div>
                    )}
                </div>
                <div className={tileClass('living', 'out')} onClick={() => toggleTile('living')} role="button" tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleTile('living'); } }}>
                    <div className="text-sm font-medium text-gray-600 mb-1">Living Expenses</div>
                    <Rule tone="out" /><dd className={valueClass('out')}>- {formatCurrency(livingExpenses)}</dd>
                    <WalkIn value={livingExpenses} baseline={baseline?.livingExpenses} upIsGood={false} />
                </div>
                <div className={tileClass('care', 'out')} onClick={() => toggleTile('care')} role="button" tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleTile('care'); } }}>
                    <div className="mb-1 text-sm font-medium text-gray-600">Total Aged Care Costs</div>
                    <Rule tone="out" /><dd className={valueClass('out')}>- {formatCurrency(Number(snapshot.totalAgedCareCosts) || 0)}</dd>
                    <WalkIn value={Number(snapshot.totalAgedCareCosts) || 0} baseline={baseline?.careCosts} upIsGood={false} />
                </div>
                <div className={tileClass('tax', taxPayable < 0 ? 'in' : 'out')} onClick={() => toggleTile('tax')} role="button" tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleTile('tax'); } }}>
                    <div className="mb-1 text-sm font-medium text-gray-600">Income Tax</div>
                    <Rule tone={taxPayable < 0 ? 'in' : 'out'} /><dd className={valueClass(taxPayable < 0 ? 'in' : 'out')}>
                        {taxPayable < 0 ? '+' : '-'} {formatCurrency(Math.abs(taxPayable))}{taxPayable < 0 ? ' (refund)' : ''}
                    </dd>
                    <WalkIn value={taxPayable} baseline={baseline?.taxPayable} upIsGood={false} />
                </div>
                <div className={tileClass('cf', 'net')} onClick={() => toggleTile('cf')} role="button" tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleTile('cf'); } }}>
                    <div className="mb-1 text-sm font-medium text-gray-600">Net Annual Cashflow</div>
                    <Rule tone="net" />
                    <dd className={`mt-0.5 text-[22px] font-bold tabular-nums leading-tight ${netCashflow >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                        {(netCashflow >= 0 ? '+' : '') + formatCurrency(netCashflow)}
                    </dd>
                    <p className="mt-0.5 text-[13px] font-medium text-slate-500">
                        {netCashflow >= 0 ? 'a year left over' : 'a year short'}
                    </p>
                    <WalkIn value={netCashflow} baseline={baseline?.netCashflow} upIsGood />
                </div>
            </div>

            {openTile && (
                /* White with a blue rail, not a pastel fill (owner, 17 Oct 2026). */
                <div className="mt-3 rounded-xl border border-slate-200 border-l-4 border-l-blue-500 bg-white px-4 py-3 shadow-card" data-testid={`snapshot-why-${openTile}`}>
                    {/* A way out that does not depend on knowing the tile toggles (owner, 22 Sep 2026:
                        "I can't see how I can collapse it again easily"). */}
                    <div className="flex items-start justify-between gap-3">
                        <p className="text-sm leading-relaxed text-slate-700">{taxPage && openTile === 'cf' ? whyFor('tax') : whyFor(openTile)}</p>
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setShowCalc(false); setTaxPage(false); setOpenTile(null); }}
                            aria-label="Close"
                            className="shrink-0 rounded-md border border-slate-200 bg-white px-2 py-0.5 text-xs font-semibold text-slate-600 hover:bg-slate-50"
                        >
                            Close
                        </button>
                    </div>
                    {showCalc ? (
                        <div className="mt-2 rounded-lg border border-slate-200 bg-white px-3 py-2">
                            {/* ONE DIALOG, TWO PAGES (owner, 17 Oct 2026): the cashflow calculation's tax line opens how the
                                tax is worked out in the same place, with a way back — not a tooltip inside a tooltip. */}
                            {taxPage && openTile === 'cf' ? (
                                <>
                                    <button type="button" onClick={(e) => { e.stopPropagation(); setTaxPage(false); }} className="mb-1 border-0 bg-transparent p-0 text-xs font-semibold text-blue-700 underline decoration-dotted underline-offset-2 cursor-pointer hover:text-blue-900">
                                        ‹ Back to the cashflow
                                    </button>
                                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">How the tax is worked out</p>
                                </>
                            ) : (
                                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">The calculation</p>
                            )}
                            {(taxPage && openTile === 'cf' ? calcRows.tax : calcRows[openTile]).map((row, index) => {
                                const isTaxLine = openTile === 'cf' && !taxPage && /^tax\b/i.test(String(row.label));
                                const isHeading = String(row.label).startsWith('---');
                                return (
                                    <div key={`${row.label}-${index}`} className={`flex justify-between gap-4 py-0.5 text-xs ${isHeading ? 'mt-1.5 border-t border-slate-100 pt-1.5' : ''}`}>
                                        {isHeading ? (
                                            <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{String(row.label).replace(/-/g, '').trim()}</span>
                                        ) : isTaxLine ? (
                                            <button type="button" onClick={(e) => { e.stopPropagation(); setTaxPage(true); }} className="border-0 bg-transparent p-0 text-left text-xs font-semibold text-blue-700 underline decoration-dotted underline-offset-2 cursor-pointer hover:text-blue-900">
                                                {row.label} — see how it is worked out ›
                                            </button>
                                        ) : (
                                            <span className="text-slate-600">{row.label}</span>
                                        )}
                                        <span className="font-semibold text-slate-900 tabular-nums">{String(row.value ?? '')}</span>
                                    </div>
                                );
                            })}
                            <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); setShowCalc(false); setTaxPage(false); }}
                                className="mt-2 border-0 bg-transparent p-0 text-xs font-semibold text-blue-700 underline decoration-dotted underline-offset-2 cursor-pointer hover:text-blue-900"
                            >
                                ‹ Hide the calculation
                            </button>
                        </div>
                    ) : (
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setShowCalc(true); }}
                            className="mt-1.5 border-0 bg-transparent p-0 text-xs font-semibold text-blue-700 underline decoration-dotted underline-offset-2 cursor-pointer hover:text-blue-900"
                        >
                            Show the calculation ›
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default AnnualSnapshot;
